import { useCallback, useEffect, useRef, useState } from 'react'
import { FileText, Trash2, Upload } from 'lucide-react'
import { erfuelltRecht } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { deleteFile, fetchFiles, uploadFile } from '../api/projects-phase-d.api'
import { oeffneProjektdatei } from '../api/project-tech.api'
import { alsGroesse, alsZeitpunkt } from '../project-labels'
import type { ProjectFileRow } from '../types/phase-d.types'

/**
 * **Auftragsdokumente** in der Karte „Kaufmännisches“ (AP-35 Nachtrag 8):
 * Bestellung des Kunden, Auftragsbestätigung, Vertrag, Leistungsverzeichnis.
 *
 * Kein eigener Speicher — es sind Projektdateien der Art `ORDER` am Projekt,
 * abgelegt über den FileService. Sie stehen damit auch in der Dateiliste des
 * Projekts, sind aber nie in der Auftragsmappe der PWA sichtbar (die Vorgabe
 * `visibleInPwa = false` bleibt). Bilder werden nicht verkleinert: Ein
 * abfotografierter Vertrag muss lesbar bleiben.
 *
 * Hochladen und Löschen mit `projects.position.manage` wie jede Projektdatei;
 * lesen darf, wer das Projekt sieht. Dateien lassen sich auch auf die Fläche
 * ziehen.
 */
export function AuftragsDokumente({ projectId }: { projectId: string }) {
  const { user } = useAuthStore()
  const darfPflegen = erfuelltRecht('projects.position.manage', user?.permissions)
  const [dateien, setDateien] = useState<ProjectFileRow[] | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [ueber, setUeber] = useState(0)
  const [loeschFrage, setLoeschFrage] = useState<string | null>(null)
  const eingabe = useRef<HTMLInputElement>(null)

  const laden = useCallback(async () => {
    try {
      setDateien(await fetchFiles(projectId, { entityType: 'PROJECT', entityId: projectId, kind: 'ORDER' }))
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Auftragsdokumente ließen sich nicht laden.'))
    }
  }, [projectId])

  useEffect(() => {
    void laden()
  }, [laden])

  async function hochladen(liste: File[]) {
    if (!liste.length) return
    setLaeuft(true)
    setFehler(null)
    const fehlgeschlagen: string[] = []
    for (const datei of liste) {
      try {
        await uploadFile(projectId, datei, { entityType: 'PROJECT', entityId: projectId, kind: 'ORDER', keepOriginal: true })
      } catch (e) {
        fehlgeschlagen.push(`${datei.name}: ${apiErrorText(e, 'nicht hochgeladen')}`)
      }
    }
    if (fehlgeschlagen.length) setFehler(fehlgeschlagen.join(' · '))
    setLaeuft(false)
    if (eingabe.current) eingabe.current.value = ''
    await laden()
  }

  async function loeschen(id: string) {
    setFehler(null)
    try {
      await deleteFile(id)
      setLoeschFrage(null)
      await laden()
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Datei ließ sich nicht löschen.'))
    }
  }

  const sindDateien = (e: React.DragEvent) => [...e.dataTransfer.types].includes('Files')

  return (
    <section
      aria-label="Auftragsdokumente"
      className={`mt-4 rounded-md border border-dashed p-2.5 ${ueber > 0 ? 'border-primary bg-primary/10' : 'border-border'}`}
      onDragEnter={
        darfPflegen
          ? (e) => {
              if (!sindDateien(e)) return
              e.preventDefault()
              setUeber((n) => n + 1)
            }
          : undefined
      }
      onDragOver={
        darfPflegen
          ? (e) => {
              if (!sindDateien(e)) return
              e.preventDefault()
              e.dataTransfer.dropEffect = 'copy'
            }
          : undefined
      }
      onDragLeave={darfPflegen ? (e) => sindDateien(e) && setUeber((n) => Math.max(0, n - 1)) : undefined}
      onDrop={
        darfPflegen
          ? (e) => {
              if (!sindDateien(e)) return
              e.preventDefault()
              setUeber(0)
              void hochladen([...e.dataTransfer.files])
            }
          : undefined
      }
    >
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-medium">Auftragsdokumente</h4>
        {darfPflegen && (
          <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs hover:bg-muted">
            <Upload className="h-3.5 w-3.5" />
            {laeuft ? 'lädt …' : 'Hochladen'}
            <input ref={eingabe} type="file" multiple className="hidden" onChange={(e) => void hochladen([...(e.target.files ?? [])])} />
          </label>
        )}
      </div>
      {fehler && <p className="mt-1.5 text-xs text-destructive">{fehler}</p>}
      {dateien && dateien.length === 0 && (
        <p className="mt-1.5 text-xs text-muted-foreground">
          Noch keine — Bestellung, Auftragsbestätigung oder Vertrag {darfPflegen ? 'hochladen oder hierher ziehen' : 'sind nicht abgelegt'}.
        </p>
      )}
      {dateien && dateien.length > 0 && (
        <ul className="mt-1.5 divide-y divide-border">
          {dateien.map((d) => (
            <li key={d.id} className="flex items-center gap-2 py-1 text-sm">
              <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1">
                {/* Kein Link: das Token steht nicht im Cookie, ein <a href> bekäme 401 (Phase F). */}
                <button
                  type="button"
                  className="block max-w-full truncate text-left font-medium text-primary hover:underline"
                  onClick={() => oeffneProjektdatei(d.id, d.file.originalName).catch((e) => setFehler(apiErrorText(e, 'Die Datei lässt sich nicht öffnen.')))}
                >
                  {d.file.originalName}
                </button>
                <span className="block text-[11px] text-muted-foreground">
                  {alsZeitpunkt(d.uploadedAt)}
                  {d.uploadedByName && ` · ${d.uploadedByName}`} · {alsGroesse(d.file.sizeBytes)}
                  {d.note && ` · ${d.note}`}
                </span>
              </span>
              {darfPflegen && !d.locked && loeschFrage !== d.id && (
                <button type="button" className="p-1 text-muted-foreground hover:text-destructive" aria-label={`${d.file.originalName} löschen`} title="Löschen" onClick={() => setLoeschFrage(d.id)}>
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
              {loeschFrage === d.id && (
                <span className="inline-flex shrink-0 items-center gap-2 text-xs">
                  <button type="button" className="rounded bg-destructive px-2 py-0.5 font-medium text-destructive-foreground" onClick={() => void loeschen(d.id)}>
                    Löschen
                  </button>
                  <button type="button" className="text-muted-foreground hover:underline" onClick={() => setLoeschFrage(null)}>
                    Abbrechen
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
